/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/removeRepo/RemoveRepoPanel" />
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { format } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ExternalConnection } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { getTextWithServiceName, getMaxRepoCount } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import { IRemoveRepoPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/removeRepo/RemoveRepoPanel.Props";
import { RemoveRepoPicker } from "Wit/Settings/BoardsExternalIntegration/components/panels/removeRepo/RemoveRepoPicker";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { GitHubIntegrationCIActions, publishGitHubIntegrationTelemetry } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";

export interface IRemoveRepoPanelState {
    selectedRepos: { [repoNodeId: string]: ExternalGitRepo };
    errorMessage?: string;
    loadingSpinnerMessage?: string;
    isDirty: boolean;
}

export class RemoveRepoPanel extends VssComponent<IRemoveRepoPanelProps, IRemoveRepoPanelState, IVssComponentContext> {
    constructor(props: IRemoveRepoPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            selectedRepos: {},
            isDirty: false
        };
    }

    public render() {
        const { externalGitRepos } = this.props.connection;

        return (
            <BasePanel
                onDismiss={this._onDismiss}
                onDismissErrorMessage={this._onDismissErrorMessage}
                title={getTextWithServiceName(this.props.repositoryType, Resources.RemoveRepo_Title)}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                errorMessage={this.state.errorMessage}
                loadingSpinnerMessage={this.state.loadingSpinnerMessage}
            >
                <RemoveRepoPicker existingRepos={externalGitRepos} onRemoveReposSelectionChanged={this._onRemoveReposSelectionChanged} />
            </BasePanel>
        );
    }

    private readonly _onDismiss = () => {
        // Don't dismiss when busy
        if (!this.state.loadingSpinnerMessage) {
            if (!this.state.isDirty) {
                this.props.onDismiss();
            } else {
                showConfirmationDialog(
                    this.context,
                    Resources.ConfirmationClosePanelMessage,
                    Resources.ConfirmationClosePanelActionText,
                    Resources.ConfirmationClosePanelTitle,
                    this.props.onDismiss
                );
            }
        }
    };

    private readonly _onSaveButtonClicked = () => {
        const { connection } = this.props;
        const { externalGitRepos } = connection;
        const { selectedRepos } = this.state;
        const updatedConnection: ExternalConnection = {
            ...connection,
            externalGitRepos: [...externalGitRepos.filter(repo => !selectedRepos[repo.id])] // Filter out selected respositories
        };

        if (updatedConnection.externalGitRepos.length === 0) {
            // Remove connection if there are no repos in it
            showConfirmationDialog(
                this.context,
                Resources.ConfirmationMessageRemoveRepoRemoveConnection,
                Resources.ConfirmationRemove,
                Resources.RemoveAllReposConfirmationTitle,
                async () => this._onSave(updatedConnection, Resources.SpinnerRemovingConnectionText)
            );
        } else {
            this._onSave(updatedConnection, Resources.SpinnerBasePanelSavingText);
        }
    };

    private readonly _onDismissErrorMessage = () => {
        this.setState({ errorMessage: undefined });
    };

    private readonly _renderHeader = () => {
        return <div>{Resources.RemoveRepo_SubHeader}</div>;
    };

    private readonly _renderFooter = (): JSX.Element => {
        return (
            <>
                <div className="align-right">
                    <Button className="footer-button" text={Resources.Cancel} onClick={this._onDismiss} />
                    <Button
                        className="footer-button"
                        text={Resources.Save}
                        primary={true}
                        disabled={!this._canSave()}
                        onClick={this._onSaveButtonClicked}
                    />
                </div>
            </>
        );
    };

    private readonly _onSave = async (connectionToRemove: ExternalConnection, spinnerMessage: string): Promise<void> => {
        this.setState({ loadingSpinnerMessage: spinnerMessage });

        let removedConnection = connectionToRemove;
        let succeeded = true;
        let errorMessage = "";
        try {
            removedConnection = await this.props.onSave(connectionToRemove);
        } catch (error) {
            succeeded = false;
            errorMessage = error.message;
            this.setState({
                loadingSpinnerMessage: undefined,
                errorMessage: error.message
            });
        } finally {
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_REMOVE_REPOS, {
                numberOfRepos: Object.keys(this.state.selectedRepos).length,
                providerKey: removedConnection.providerKey,
                authType: removedConnection.serviceEndpoint.authorizationScheme,
                succeeded: succeeded,
                errorMessage: errorMessage
            });
        }
    };

    private readonly _onRemoveReposSelectionChanged = (selectedRepos: { [repoNodeId: string]: ExternalGitRepo }) => {
        this.setState({
            selectedRepos: selectedRepos,
            isDirty: Object.keys(selectedRepos).length > 0,
            errorMessage: this._isReposCountExceedLimit(selectedRepos)
                ? format(Resources.ExceededMaxReposCountPerConnection, getMaxRepoCount(this.context.pageContext))
                : undefined
        });
    };

    private _canSave(): boolean {
        return this.state.isDirty && !this._isReposCountExceedLimit(this.state.selectedRepos);
    }

    private _isReposCountExceedLimit(selectedRepos: { [repoNodeId: string]: ExternalGitRepo }): boolean {
        const existingRepoCount =
            (this.props.connection && this.props.connection.externalGitRepos && this.props.connection.externalGitRepos.length) || 0;
        const toRemoveRepoCount = (selectedRepos && Object.keys(selectedRepos).length) || 0;
        const targetRepoCount = existingRepoCount - toRemoveRepoCount;

        return targetRepoCount > getMaxRepoCount(this.context.pageContext);
    }
}
